// @ts-nocheck
/**
 * compositionRenderers.ts — Template V2 M3 Layout / Composition Renderers
 *
 * Exports typed ComponentRenderer implementations for:
 *   GROUP_RENDERER  — 'group'  (recurses into childIds via childRenderer)
 *   BUTTON_RENDERER — 'button' (validates action reference from compiledTemplate)
 *   ICON_RENDERER   — 'icon'   (catalog glyph; optional press handler)
 *   SPACER_RENDERER — 'spacer' (minimal flex/gap node)
 *   DRAG_HANDLE_RENDERER — 'dragHandle' (visual grip affordance, no data props)
 *   LINE_RENDERER — 'line' (decorative horizontal/vertical stroke)
 *
 * No React/RN imports; output is a plain serializable RenderDescriptor.
 */

import { unwrapValueSource } from '../binding/bindingContract';
import { normalizeEventHandler } from '../../types/eventHandler';
import { isIconId } from '../../icons/iconCatalog';
import { applyExpressionFallback, buildDescriptor, coerceResolvedDisplayValue, resolveValue } from './rendererUtils';
import { collectPressHandlers } from './componentGestureHandlers';
import { componentInteractionProps } from './interactionProps';
import type {
  CompiledPropsByType,
  ComponentRenderContext,
  ComponentRenderer,
  RenderDescriptor,
} from './types';
import { extractTypedProps } from './types';

const DEFAULT_LINE_STROKE_WIDTH_PX = 2;
const DEFAULT_LINE_COLOR = '#64748b';

function pushPressHandlerDiagnostics(
  context: ComponentRenderContext<unknown>,
  pressHandler: ReturnType<typeof normalizeEventHandler>,
  label: string,
): void {
  if (!pressHandler) {
    context.issues.push({
      severity: 'warning',
      code: 'semantic.invalid_event_handler',
      message: `${label} renderer: invalid press handler for node "${context.node.id}".`,
      path: context.node.id,
    });
  } else if (pressHandler.kind === 'action') {
    const { actionId } = pressHandler;
    if (!Object.prototype.hasOwnProperty.call(context.compiledTemplate.actionsById, actionId)) {
      context.issues.push({
        severity: 'warning',
        code: 'semantic.invalid_action_reference',
        message:
          `${label} renderer: action "${actionId}" not found in compiled template ` +
          `for node "${context.node.id}".`,
        path: context.node.id,
      });
    }
  } else if (pressHandler.kind === 'animation') {
    const { animationId } = pressHandler;
    const animations = (context.compiledTemplate as { animations?: readonly { id: string }[] }).animations;
    if (
      animations !== undefined &&
      !animations.some((animation) => animation.id === animationId)
    ) {
      context.issues.push({
        severity: 'warning',
        code: 'semantic.invalid_animation_reference',
        message:
          `${label} renderer: animation "${animationId}" not found in compiled template ` +
          `for node "${context.node.id}".`,
        path: context.node.id,
      });
    }
  } else if (pressHandler.kind === 'script') {
    if (!Object.prototype.hasOwnProperty.call(context.compiledTemplate.scriptsById, pressHandler.scriptId)) {
      context.issues.push({
        severity: 'warning',
        code: 'semantic.invalid_script_reference',
        message:
          `${label} renderer: script "${pressHandler.scriptId}" not found in compiled template ` +
          `for node "${context.node.id}".`,
        path: context.node.id,
      });
    }
  }
}

function isValidColorString(value: string): boolean {
  if (/^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value)) return true;
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(value)) return true;
  return false;
}

function resolveLineStrokeWidthPx(
  strokeWidthSource: CompiledPropsByType['line']['line']['strokeWidth'],
  context: Parameters<ComponentRenderer<CompiledPropsByType['line']>['render']>[0],
): number {
  if (strokeWidthSource == null) {
    return DEFAULT_LINE_STROKE_WIDTH_PX;
  }
  const raw = resolveValue(strokeWidthSource, context);
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
    return raw;
  }
  if (raw !== undefined) {
    context.issues.push({
      severity: 'warning',
      code: 'schema.invalid_field_type',
      message:
        `Line strokeWidth "${String(raw)}" is not a positive number for node "${context.node.id}"; ` +
        `falling back to ${DEFAULT_LINE_STROKE_WIDTH_PX}px.`,
      path: context.node.id,
    });
  }
  return DEFAULT_LINE_STROKE_WIDTH_PX;
}

function resolveLineColorValue(
  colorSource: CompiledPropsByType['line']['line']['color'],
  context: Parameters<ComponentRenderer<CompiledPropsByType['line']>['render']>[0],
): string | null {
  if (colorSource == null) {
    return DEFAULT_LINE_COLOR;
  }
  const raw = resolveValue(colorSource, context);
  if (raw === undefined || raw === null) {
    return null;
  }
  if (typeof raw === 'string' && isValidColorString(raw)) {
    return raw;
  }
  context.issues.push({
    severity: 'warning',
    code: 'schema.invalid_field_type',
    message:
      `Line color "${String(raw)}" is not a valid CSS color for node "${context.node.id}"; ` +
      'falling back to null.',
    path: context.node.id,
  });
  return null;
}

// ── Group ─────────────────────────────────────────────────────────────────────

export const GROUP_RENDERER: ComponentRenderer<CompiledPropsByType['group']> = {
  supportedType: 'group',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('group', context.node.props);

    // Recurse into child nodes in declaration order
    const children: RenderDescriptor[] = [];
    for (const childId of context.node.childIds) {
      const child = context.childRenderer(childId);
      if (child !== null) {
        children.push(child);
      }
    }

    return buildDescriptor(context.node, visible, enabled, {
      clipContent: p.group?.clipContent ?? false,
    }, {
      children,
      diagnostics: context.issues.slice(issuesBefore),
      renderContext: context,
    });
  },
};

// ── Button ────────────────────────────────────────────────────────────────────

export const BUTTON_RENDERER: ComponentRenderer<CompiledPropsByType['button']> = {
  supportedType: 'button',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('button', context.node.props);
    const labelSource = unwrapValueSource<string>(p.button.label) ?? p.button.label;
    const label = coerceResolvedDisplayValue(resolveValue(labelSource, context), context.locale);

    const hrefRaw = p.button.href ? resolveValue(p.button.href, context) : null;
    const href =
      typeof hrefRaw === 'string' && hrefRaw.trim().length > 0 ? hrefRaw.trim() : null;

    const pressHandler = normalizeEventHandler(p.button.action);
    if (pressHandler != null || href == null) {
      pushPressHandlerDiagnostics(context, pressHandler, 'Button');
    }

    const pressHandlers = collectPressHandlers(context.node);
    return buildDescriptor(context.node, visible, enabled, {
      label: label ?? null,
      href,
      icon: p.button.icon ?? null,
      variant: p.button.variant ?? 'primary',
      pressHandler: pressHandlers[0] ?? p.button.action ?? null,
      pressHandlers,
      ...componentInteractionProps(context.node),
      confirm: p.button.confirm ?? null,
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};

// ── Icon ──────────────────────────────────────────────────────────────────────

export const ICON_RENDERER: ComponentRenderer<CompiledPropsByType['icon']> = {
  supportedType: 'icon',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('icon', context.node.props);
    const nameSource = unwrapValueSource<string>(p.icon.name) ?? p.icon.name;
    const resolvedName = resolveValue(nameSource, context);
    const name =
      typeof resolvedName === 'string' && resolvedName.length > 0 ? resolvedName : null;

    if (name == null || !isIconId(name)) {
      context.issues.push({
        severity: 'warning',
        code: 'schema.invalid_field_type',
        message:
          `Icon renderer: unknown or missing catalog id "${String(resolvedName)}" ` +
          `for node "${context.node.id}".`,
        path: context.node.id,
      });
    }

    if (p.icon.action != null) {
      const pressHandler = normalizeEventHandler(p.icon.action);
      pushPressHandlerDiagnostics(context, pressHandler, 'Icon');
    }

    const pressHandlers = collectPressHandlers(context.node);
    return buildDescriptor(context.node, visible, enabled, {
      name,
      pressHandler: pressHandlers[0] ?? p.icon.action ?? null,
      pressHandlers,
      ...componentInteractionProps(context.node),
      confirm: p.icon.confirm ?? null,
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};

// ── Spacer ────────────────────────────────────────────────────────────────────

export const SPACER_RENDERER: ComponentRenderer<CompiledPropsByType['spacer']> = {
  supportedType: 'spacer',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('spacer', context.node.props);
    return buildDescriptor(context.node, visible, enabled, {
      flex: p.spacer?.flex ?? null,
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};

// ── Drag Handle ───────────────────────────────────────────────────────────────

export const DRAG_HANDLE_RENDERER: ComponentRenderer<CompiledPropsByType['dragHandle']> = {
  supportedType: 'dragHandle',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    extractTypedProps('dragHandle', context.node.props);
    return buildDescriptor(context.node, visible, enabled, {}, {
      diagnostics: context.issues.slice(issuesBefore),
      renderContext: context,
    });
  },
};

// ── Line ──────────────────────────────────────────────────────────────────────

export const LINE_RENDERER: ComponentRenderer<CompiledPropsByType['line']> = {
  supportedType: 'line',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('line', context.node.props);
    const orientation = p.line?.orientation === 'vertical' ? 'vertical' : 'horizontal';
    const strokeWidthPx = resolveLineStrokeWidthPx(p.line?.strokeWidth, context);
    const colorValue = resolveLineColorValue(p.line?.color, context);

    return buildDescriptor(context.node, visible, enabled, {
      orientation,
      strokeWidthPx,
      colorValue,
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};
